import { forwardRef, type ReactNode } from "react";
import { Box, Stack, useMediaQuery, useTheme } from "@mui/material";
import { Breadcrumbs as FoodsBreadcrumbs, Button, FoodsIcon, Typography, WhitePopover } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";

import { Breadcrumbs as ResponsiveBreadcrumbs, type BreadcrumbType } from "src/components/Breadcrumb/Breadcrumbs";
import { FoodsLoadingIndicator } from "./FoodsFields";

export type FormHeaderMetadata = {
    label: ReactNode;
    value: ReactNode;
};

export type FormHeaderSecondaryAction = {
    disabled?: boolean;
    endIcon?: ReactNode;
    label: ReactNode;
    onClick: () => void;
    startIcon?: ReactNode;
};

export type FormHeaderProps = {
    breadcrumbs: BreadcrumbType[];
    title: ReactNode;
    subtitle?: ReactNode;
    metadata?: FormHeaderMetadata;
    onCancel: () => void;
    onSave: () => void;
    saving?: boolean;
    saveDisabled?: boolean;
    saveDisabledReason?: ReactNode;
    saveDisabledWarning?: ReactNode;
    cancelDisabled?: boolean;
    cancelLabel?: ReactNode;
    saveEndIcon?: ReactNode;
    saveLabel?: ReactNode;
    saveStartIcon?: ReactNode | null;
    secondaryAction?: FormHeaderSecondaryAction;
    testIdPrefix?: string;
    sticky?: boolean;
};

/** Shared sticky breadcrumb/title/actions chrome for entity create and edit forms. */
export const FormHeader = forwardRef<HTMLElement, FormHeaderProps>((props, ref) => {
    const {
        breadcrumbs,
        title,
        subtitle,
        metadata,
        onCancel,
        onSave,
        saving = false,
        saveDisabled = false,
        saveDisabledReason,
        saveDisabledWarning,
        cancelDisabled,
        cancelLabel,
        saveEndIcon,
        saveLabel,
        saveStartIcon,
        secondaryAction,
        testIdPrefix = "entity-form",
        sticky = true,
    } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
    const isCancelDisabled = cancelDisabled ?? saving;
    const resolvedSaveStartIcon = saveStartIcon === undefined ? <FoodsIcon icon="check" size={2} /> : saveStartIcon;
    const disabledSavePopoverWidth = theme.custom.tooltip.simple.width;
    const disabledSavePopoverArrowOffset = Number.parseFloat(disabledSavePopoverWidth) / 2;
    const actionButtonSx = {
        flex: isMobile ? 1 : undefined,
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.cta.fontSize,
        fontStyle: theme.typography.cta.fontStyle,
        fontWeight: theme.typography.cta.fontWeight,
        gap: theme.spacing(1),
        letterSpacing: theme.typography.cta.letterSpacing,
        lineHeight: theme.typography.cta.lineHeight,
        paddingInlineEnd: theme.spacing(2.5),
        paddingInlineStart: theme.spacing(2),
    };
    const currentBreadcrumb = breadcrumbs.at(-1);
    const hasDisabledBreadcrumbAncestor = breadcrumbs.some((breadcrumb) => breadcrumb.action && breadcrumb.disabled);
    const breadcrumbAncestors = breadcrumbs.slice(0, -1).flatMap((breadcrumb, index) =>
        breadcrumb.action ?
            [
                {
                    id: `${testIdPrefix}-breadcrumb-ancestor-${index}`,
                    label: breadcrumb.label,
                    onSelect: () => {
                        if (!breadcrumb.disabled) breadcrumb.action?.();
                    },
                },
            ]
        :   [],
    );
    const saveAction = (
        <Button
            variant="validate"
            onClick={onSave}
            disabled={saving || saveDisabled}
            aria-busy={saving}
            startIcon={
                saving ?
                    <Box
                        component="span"
                        sx={{ display: "inline-flex", width: theme.spacing(2), height: theme.spacing(2) }}
                    >
                        <FoodsLoadingIndicator label={t("common.actions.saving", "Saving...")} color="currentColor" />
                    </Box>
                :   resolvedSaveStartIcon
            }
            endIcon={saving ? undefined : saveEndIcon}
            sx={actionButtonSx}
        >
            {saveLabel ?? t("common.actions.save", "Save")}
        </Button>
    );
    const resolvedSaveAction =
        saveDisabled && saveDisabledReason !== undefined ?
            <WhitePopover
                icon={saveAction}
                body={
                    <Box sx={{ padding: theme.spacing(1.5, 2) }}>
                        <Typography variant="body-bold">{saveDisabledWarning ?? ""}</Typography>
                        <br />
                        <Typography variant="body">{saveDisabledReason}</Typography>
                    </Box>
                }
                size={disabledSavePopoverWidth}
                leftPosition={disabledSavePopoverArrowOffset}
            />
        :   saveAction;

    return (
        <Box
            ref={ref}
            component="header"
            data-testid={`${testIdPrefix}-form-header`}
            sx={{
                position: sticky ? "sticky" : "relative",
                top: sticky ? 0 : undefined,
                zIndex: theme.zIndex.appBar,
                backgroundColor: theme.custom.grey[300],
                boxShadow: "none",
            }}
        >
            <Box
                data-testid={`${testIdPrefix}-breadcrumb-row`}
                sx={{
                    position: "relative",
                    backgroundColor: theme.custom.grey[100],
                    padding: isMobile ? theme.spacing(3, 2) : theme.spacing(3, 6),
                    "&::after": {
                        content: '""',
                        position: "absolute",
                        insetInline: 0,
                        bottom: 0,
                        height: theme.custom.strokes.containerDivider.width,
                        backgroundColor: theme.custom.strokes.containerDivider.color,
                    },
                }}
            >
                {currentBreadcrumb &&
                    (isMobile ?
                        <ResponsiveBreadcrumbs appearance="product-v2" items={breadcrumbs} compact />
                    :   <Box
                            aria-disabled={hasDisabledBreadcrumbAncestor || undefined}
                            sx={
                                hasDisabledBreadcrumbAncestor ?
                                    { "& button": { cursor: "default", pointerEvents: "none" } }
                                :   undefined
                            }
                        >
                            <FoodsBreadcrumbs
                                ariaLabel={t("common.navigation.breadcrumb", "Breadcrumb")}
                                ancestors={breadcrumbAncestors}
                                current={{ id: `${testIdPrefix}-breadcrumb-current`, label: currentBreadcrumb.label }}
                            />
                        </Box>)}
            </Box>

            <Stack
                data-testid={`${testIdPrefix}-title-row`}
                direction={isMobile ? "column" : "row"}
                alignItems="flex-start"
                justifyContent="space-between"
                sx={{ gap: theme.spacing(3), padding: isMobile ? theme.spacing(3, 2) : theme.spacing(3, 6) }}
            >
                <Stack
                    direction={isMobile ? "column" : "row"}
                    alignItems={isMobile ? "flex-start" : "center"}
                    flex={isMobile ? undefined : 1}
                    minWidth={0}
                    sx={{ gap: theme.spacing(2), width: isMobile ? "100%" : undefined }}
                >
                    <Stack
                        minWidth={0}
                        maxWidth="100%"
                        sx={{ gap: theme.spacing(0.5), width: isMobile ? "100%" : undefined }}
                    >
                        <Typography
                            variant="h2"
                            sx={{ color: theme.custom.grey[2800], overflowWrap: "anywhere", whiteSpace: "normal" }}
                        >
                            {title}
                        </Typography>
                        {subtitle && (
                            <Typography
                                variant="small-regular"
                                sx={{ color: theme.custom.grey[2000], overflowWrap: "anywhere", whiteSpace: "normal" }}
                            >
                                {subtitle}
                            </Typography>
                        )}
                    </Stack>

                    {metadata && (
                        <>
                            <Box
                                aria-hidden
                                sx={{
                                    alignSelf: "stretch",
                                    backgroundColor: theme.custom.strokes.containerDivider.color,
                                    flexShrink: 0,
                                    height: isMobile ? theme.custom.strokes.containerDivider.width : "auto",
                                    width: isMobile ? "100%" : theme.custom.strokes.containerDivider.width,
                                }}
                            />
                            <Stack direction="row" alignItems="center" minWidth={0} sx={{ gap: theme.spacing(1) }}>
                                <Typography variant="small-medium" sx={{ color: theme.custom.grey[2800] }} noWrap>
                                    {metadata.label}
                                </Typography>
                                <Typography variant="small-regular" sx={{ color: theme.custom.grey[2500] }} noWrap>
                                    {metadata.value}
                                </Typography>
                            </Stack>
                        </>
                    )}
                </Stack>

                <Stack
                    direction="row"
                    alignItems="center"
                    sx={{
                        gap: theme.spacing(2),
                        width: isMobile ? "100%" : undefined,
                    }}
                >
                    {secondaryAction && (
                        <Button
                            variant="text"
                            onClick={secondaryAction.onClick}
                            disabled={secondaryAction.disabled}
                            startIcon={secondaryAction.startIcon}
                            endIcon={secondaryAction.endIcon}
                            sx={actionButtonSx}
                        >
                            {secondaryAction.label}
                        </Button>
                    )}
                    <Button variant="secondary" onClick={onCancel} disabled={isCancelDisabled} sx={actionButtonSx}>
                        <FoodsIcon icon="close" size={2} />
                        {cancelLabel ?? t("common.actions.cancel", "Cancel")}
                    </Button>
                    {resolvedSaveAction}
                </Stack>
            </Stack>
        </Box>
    );
});

FormHeader.displayName = "FormHeader";
